/**
 * チェックルール管理 — the rulebook, and where its rules come from.
 *
 * These rules are what catch 「他院より効果が高い」 for free, on every article,
 * with the same wording every time - no model call, no corpus lookup. They are
 * the cheap half of the legal check; the corpus is the half that reads.
 *
 * The screen is shaped around the decision an approver actually makes, which is
 * not "is this rule correct" but "what would accepting it do":
 *
 *   the CITATION - the provision the miner read, quoted, so the rule can be
 *   checked against the law rather than trusted;
 *
 *   the IMPACT - how many existing articles the pattern matches. A rule firing
 *   on a third of them is either a real systemic problem or a bad pattern, and
 *   either way it has to be seen before approval. Counts only: a JMC session
 *   cannot read a clinic's article, and this screen does not change that;
 *
 *   the LABELLED CASES - what the set gets right and wrong, in both directions.
 *   A set that catches everything by flagging everything cannot be activated.
 *
 * The working draft is implicit. A rulebook is edited by copying the live set,
 * changing the copy and activating it - which is what keeps 「どのルールで指摘
 * されたか」 answerable a year later - but that is the MECHANISM, and it used to
 * be the interface too: nothing could be approved until somebody understood it
 * and pressed 下書きを作成. Now the draft is made when it is first needed and
 * named in one line at the top; the picker appears only if there are two.
 */
import { useEffect, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router';
import {
  ApiError,
  del,
  get,
  post,
  type ActiveRulebook,
  type ListResponse,
  type MineResult,
  type Rule,
  type RuleCandidate,
  type RuleCheckResult,
  type RuleEvalCase,
  type RuleSet,
} from '../api.ts';
import { Card, Checklist, Empty, Pill, Stat, Table, Tabs, jaDate, jaNumber, rowOpen } from '../components/ui.tsx';
import { InfoButton } from '../components/InfoButton.tsx';
import { RuleDetail } from '../components/RuleDetail.tsx';
import { Modal, ModalForm } from '../components/Modal.tsx';
import { Field } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';

interface LoaderData {
  sets: ListResponse<RuleSet> & { pendingCandidates: number };
  active: ActiveRulebook;
  candidates: ListResponse<RuleCandidate>;
  evalCases: ListResponse<RuleEvalCase>;
}

type TabId = 'candidates' | 'rules' | 'cases' | 'history';

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'muted'> = {
  ACTIVE: 'ok',
  DRAFT: 'warn',
  ROLLED_BACK: 'muted',
};

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '本番',
  DRAFT: '下書き',
  ROLLED_BACK: '旧版',
};

const SEVERITY_LABEL: Record<string, string> = {
  REVIEW: '要確認',
  UNSUPPORTED: '根拠不足',
};

/** What accepting a candidate would do, in the terms an approver decides on. */
function Impact({ impact }: { impact: RuleCandidate['impact'] }) {
  const share =
    impact.totalArticles === 0
      ? 0
      : Math.round((impact.matchedArticles / impact.totalArticles) * 100);
  return (
    <>
      既存記事 {jaNumber(impact.matchedArticles)} / {jaNumber(impact.totalArticles)} 件
      {impact.totalArticles > 0 && `（${share}%）`}
      {/* The dangerous candidate is not the wrong one, it is the broad one. */}
      {impact.broad && (
        <>
          {' '}
          <Pill tone="danger">広すぎる可能性</Pill>
        </>
      )}
      {impact.breaksCases.length > 0 && (
        <>
          {' '}
          <Pill tone="danger">誤検出 {impact.breaksCases.length}</Pill>
          <span className="muted small" style={{ display: 'block' }}>
            {impact.breaksCases.map((text) => `「${text}」`).join('、')}
          </span>
        </>
      )}
      {!impact.matchesSource && (
        <>
          {' '}
          {/* A pattern that does not match the provision it came from is usually
              a paraphrase of the rule rather than the rule. */}
          <Pill tone="warn">出典に一致せず</Pill>
        </>
      )}
    </>
  );
}

export default function AdminRulebook() {
  const { sets, active, candidates, evalCases } = useLoaderData<LoaderData>();
  const revalidator = useRevalidator();

  const drafts = sets.items.filter((set) => set.status === 'DRAFT');
  const pending = candidates.items.filter((candidate) => candidate.status === 'PENDING');
  const activeSlugs = new Set(active.rules.map((rule) => rule.slug));

  const [tab, setTab] = useState<TabId>(pending.length > 0 ? 'candidates' : 'rules');
  const [draftId, setDraftId] = useState(drafts[0]?.id ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [check, setCheck] = useState<RuleCheckResult | null>(null);
  const [rejectNote, setRejectNote] = useState<Record<string, string>>({});
  const [addingCase, setAddingCase] = useState(false);
  const [newCase, setNewCase] = useState({ text: '', expectSlug: '', shouldFlag: true });
  const [addingRule, setAddingRule] = useState(false);
  const [newRule, setNewRule] = useState({
    slug: '',
    title: '',
    guidance: '',
    fix: '',
    severity: 'REVIEW',
    patterns: '',
    law: '医療広告ガイドライン',
    article: '',
  });
  /** The rules of the draft being worked on, and the one opened from a row. */
  const [draftRules, setDraftRules] = useState<Rule[]>([]);
  const [openRule, setOpenRule] = useState<Rule | null>(null);
  const [openSet, setOpenSet] = useState<RuleSet | null>(null);
  const [openSetRules, setOpenSetRules] = useState<Rule[] | null>(null);

  const draft = drafts.find((set) => set.id === draftId) ?? drafts[0];

  const report = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError ? [err.message, ...err.fields].join(' / ') : fallback);

  const run = async (key: string, work: () => Promise<string | null>) => {
    setBusy(key);
    setError(null);
    setNotice(null);
    try {
      const message = await work();
      if (message) setNotice(message);
      await revalidator.revalidate();
    } catch (err) {
      report(err, '実行できませんでした');
    } finally {
      setBusy(null);
    }
  };

  /*
   * The draft, made if it does not exist yet.
   *
   * Approving a candidate and adding a rule both need somewhere to put it, and
   * "create a draft first" is a mechanism, not a decision - so it happens on the
   * way rather than being asked for. What IS a decision, activating it, stays a
   * separate press.
   */
  const workingDraft = async (): Promise<string> => {
    if (draft) return draft.id;
    const made = await post<{ id: string; version_no: number }>('/admin/rulebook/sets', {});
    setDraftId(made.id);
    return made.id;
  };

  /*
   * Open a production rule for editing.
   *
   * The active set is immutable by design - a stored verdict was produced by
   * it - so editing it always means "start (or reuse) the draft, then edit the
   * copy in it". This used to dead-end: the production table had no click
   * handler at all, so a rule that needed a small correction had no way in
   * except knowing to press 「＋ルールを追加」 and recreate it from scratch.
   *
   * Fetched directly rather than waiting on the draftRules effect below, which
   * only starts loading once `draft` has re-rendered - a rule clicked the
   * moment its draft is created would otherwise open before that state exists.
   */
  const openActiveRule = (rule: { slug: string }) =>
    run(`open-${rule.slug}`, async () => {
      const setId = await workingDraft();
      const body = await get<{ rules: Rule[] }>(`/admin/rulebook/sets/${setId}`);
      const copy = body.rules.find((candidate) => candidate.slug === rule.slug);
      if (copy) {
        setDraftRules(body.rules);
        setOpenRule(copy);
      }
      return null;
    });

  // The draft's rules, which is what the ルール tab shows when one exists.
  useEffect(() => {
    if (!draft) {
      setDraftRules([]);
      return;
    }
    let live = true;
    void get<{ rules: Rule[] }>(`/admin/rulebook/sets/${draft.id}`)
      .then((body) => {
        if (live) setDraftRules(body.rules);
      })
      .catch(() => {
        if (live) setDraftRules([]);
      });
    return () => {
      live = false;
    };
  }, [draft, revalidator.state]);

  const added = draftRules.filter((rule) => !activeSlugs.has(rule.slug));
  const inherited = draftRules.filter((rule) => activeSlugs.has(rule.slug));
  /*
   * Rules the live set has and this draft does not.
   *
   * A draft is a copy of what was live WHEN IT WAS MADE, so a draft left open
   * while somebody else activates a change is now missing that change - and
   * activating it would remove those rules without saying so. Observed here: two
   * drafts of eight rules sitting behind a live set of twelve, four of them
   * mined and approved in between.
   *
   * Named rather than merged automatically: dropping a rule is a legitimate
   * thing to do deliberately, and guessing which it was is not something a
   * screen should do on somebody's behalf.
   */
  const draftSlugs = new Set(draftRules.map((rule) => rule.slug));
  const dropped = draft ? active.rules.filter((rule) => !draftSlugs.has(rule.slug)) : [];

  return (
    <>
      <Card
        title="チェックルール管理"
        caption="記事チェックの最初に走る、モデルを使わない指摘です。"
        actions={
          <div className="chips">
            <InfoButton title="チェックルールの使い方">
              <p>
                ここのルールは<strong>正規表現</strong>です。記事の本文に一致した時点で指摘になり、
                モデルは呼ばれません。速く、無料で、毎回同じ結果になります。
                言い換えや文脈が必要な指摘は、法令コーパス（RAG）の担当です。
              </p>

              <h3 className="infobody__title">ルールができるまで</h3>
              <Checklist
                items={[
                  '「法令から候補を抽出」で、有効なコーパスの条文を読んでルール候補を作る',
                  '候補は自動では有効にならない。引用が条文と一致しないもの、正規表現が壊れているもの、既存と重複するものは一覧に届く前に破棄される',
                  '承認したルールは下書きに入る（下書きは本番のコピーとして自動で作られる）',
                  '評価ケースで採点し、合格した下書きだけを有効化できる',
                  '有効化すると、次のチェックからそのセットが使われる',
                ]}
              />

              <h3 className="infobody__title">なぜ本番のルールを直接編集できないのか</h3>
              <p className="small">
                有効なセットは、過去の指摘が「どのルールで出たのか」を説明する記録です。
                書き換えると、先月の指摘が存在しないルールを指すことになります。
                変更は下書きに対して行い、有効化で切り替えます。前の版はロールバックできます。
              </p>

              <h3 className="infobody__title">評価ケースの読み方</h3>
              <p className="small">
                <strong>検出されてはいけない文</strong>が同じくらい重要です。
                「効果」だけに一致するルールは、違反も正しい文もすべて指摘します。
                すべてを指摘するセットは、誰にも読まれないセットになります。
              </p>
            </InfoButton>
          </div>
        }
      >
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        {notice && !error && (
          <p className="muted small" role="status">
            {notice}
          </p>
        )}

        {/*
          The four figures this screen is about, on the same deep tile the other
          detail screens lead with: what is live, what is waiting, and whether
          there is an unactivated change sitting in a draft.
        */}
        <div className="grid grid--4">
          <Stat
            label="本番セット"
            value={active.versionNo === null ? '未設定' : `v${active.versionNo}`}
            small
          />
          <Stat label="本番のルール" value={jaNumber(active.rules.length)} small />
          <Stat label="未処理の候補" value={jaNumber(pending.length)} small />
          <Stat
            label="評価ケース"
            value={jaNumber(evalCases.items.filter((one) => one.is_active).length)}
            small
          />
        </div>

        {/*
          The draft, stated rather than chosen. It is made on demand, so most of
          the time this line simply says what is pending activation.
        */}
        {draft && (
          <p className="notice" style={{ marginTop: 12 }}>
            下書き <strong>v{draft.version_no}</strong>（{jaNumber(draft.rules)} ルール
            {added.length > 0 && `・新規 ${jaNumber(added.length)}`}
            {dropped.length > 0 && `・本番から欠落 ${jaNumber(dropped.length)}`}）を編集中です。
            有効化するまで記事チェックは本番セットのままです。
            {drafts.length > 1 && (
              <span style={{ display: 'inline-block', marginLeft: 8, minWidth: 200 }}>
                <Select
                  id="draft"
                  value={draft.id}
                  onChange={setDraftId}
                  options={drafts.map((set) => ({
                    value: set.id,
                    label: `v${set.version_no}（${set.rules} ルール）`,
                  }))}
                />
              </span>
            )}
          </p>
        )}

        <Tabs
          tabs={[
            { id: 'candidates', label: `候補（${pending.length}）` },
            { id: 'rules', label: `ルール（${draft ? draftRules.length : active.rules.length}）` },
            { id: 'cases', label: `評価ケース（${evalCases.items.length}）` },
            { id: 'history', label: `履歴（${sets.items.length}）` },
          ]}
          active={tab}
          onChange={setTab}
        />

        {/* ------------------------------------------------------ candidates */}
        {tab === 'candidates' && (
          <>
            <div className="tenantbar">
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy !== null}
                onClick={() =>
                  void run('mine', async () => {
                    const mined = await post<MineResult>('/admin/rulebook/mine', {
                      limitChunks: 60,
                    });
                    const reasons = Object.entries(mined.rejections)
                      .map(([reason, count]) => `${reason} ${count}`)
                      .join('、');
                    return (
                      `${mined.chunksRead} チャンクを読み、${mined.queued} 件を候補に追加しました` +
                      `（提案 ${mined.proposed}・破棄 ${mined.rejected}${reasons ? `: ${reasons}` : ''}` +
                      `・約 $${(mined.microCost / 1_000_000).toFixed(4)}）`
                    );
                  })
                }
              >
                {busy === 'mine' ? '解析中…' : '法令から候補を抽出'}
              </button>
              <span className="muted small">
                有効なコーパスの条文を読み、ルール候補を提案します。承認するまで何も変わりません。
              </span>
            </div>

            {pending.length === 0 ? (
              <Empty>
                未処理の候補はありません。「法令から候補を抽出」で、有効なコーパスから提案を作成できます。
              </Empty>
            ) : (
              <div style={{ display: 'grid', gap: 10 }}>
                {pending.map((candidate) => (
                  <div className="panel" key={candidate.id}>
                    <h3 className="panel__title">
                      {candidate.title}{' '}
                      <Pill tone={candidate.severity === 'REVIEW' ? 'warn' : 'muted'}>
                        {SEVERITY_LABEL[candidate.severity]}
                      </Pill>
                    </h3>
                    <p className="muted small" style={{ marginTop: 0 }}>
                      {candidate.slug}
                    </p>
                    <p style={{ margin: '0 0 4px' }}>{candidate.guidance}</p>
                    {candidate.fix && (
                      <p className="small" style={{ margin: '0 0 4px' }}>
                        修正案：{candidate.fix}
                      </p>
                    )}

                    <p className="small" style={{ margin: '0 0 4px' }}>
                      検出パターン：
                      {candidate.patterns.map((pattern) => (
                        <code key={pattern} className="small" style={{ marginRight: 8 }}>
                          {pattern}
                        </code>
                      ))}
                    </p>

                    <p className="muted small" style={{ margin: '0 0 4px' }}>
                      <Impact impact={candidate.impact} />
                    </p>

                    <details>
                      <summary className="muted small">
                        出典：{candidate.law}
                        {candidate.source_heading ? `／${candidate.source_heading}` : ''}
                      </summary>
                      <p className="small" style={{ whiteSpace: 'pre-wrap' }}>
                        {candidate.source_excerpt}
                      </p>
                    </details>

                    <div className="field" style={{ marginTop: 6, marginBottom: 6 }}>
                      <label htmlFor={`note-${candidate.id}`}>却下する理由</label>
                      <input
                        id={`note-${candidate.id}`}
                        maxLength={500}
                        placeholder="例）既存ルールと重複／パターンが広すぎる"
                        value={rejectNote[candidate.id] ?? ''}
                        onChange={(event) =>
                          setRejectNote({ ...rejectNote, [candidate.id]: event.target.value })
                        }
                      />
                      <span className="field__hint">
                        次回の抽出でも読み返されます。「広すぎる」は再提案を止める判断材料になります。
                      </span>
                    </div>

                    <div className="editpanel__actions">
                      <button
                        className="btn btn--sm"
                        type="button"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(candidate.id, async () => {
                            const setId = await workingDraft();
                            await post(`/admin/rulebook/candidates/${candidate.id}/approve`, {
                              ruleSetId: setId,
                            });
                            return `${candidate.slug} を下書きに追加しました。ルールタブで内容を調整できます。`;
                          })
                        }
                      >
                        承認して下書きへ
                      </button>
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(`reject-${candidate.id}`, async () => {
                            await post(`/admin/rulebook/candidates/${candidate.id}/reject`, {
                              note: (rejectNote[candidate.id] ?? '').trim() || '理由未記入',
                            });
                            return `${candidate.slug} を却下しました。`;
                          })
                        }
                      >
                        却下
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ----------------------------------------------------------- rules */}
        {tab === 'rules' && (
          <>
            <div className="tenantbar">
              <button
                className="btn btn--sm"
                type="button"
                disabled={busy !== null}
                onClick={() => {
                  setNewRule({
                    slug: '',
                    title: '',
                    guidance: '',
                    fix: '',
                    severity: 'REVIEW',
                    patterns: '',
                    law: '医療広告ガイドライン',
                    article: '',
                  });
                  setAddingRule(true);
                }}
              >
                ＋ ルールを追加
              </button>
              {draft && (
                <>
                  <button
                    className="btn btn--ghost btn--sm"
                    type="button"
                    disabled={busy !== null}
                    onClick={() =>
                      void run('check-draft', async () => {
                        const result = await post<RuleCheckResult>(
                          `/admin/rulebook/sets/${draft.id}/check`,
                          {},
                        );
                        setCheck(result);
                        return result.status === 'PASSED'
                          ? `v${draft.version_no}: 評価ケース ${result.passed}/${result.total} 合格。有効化できます。`
                          : `v${draft.version_no}: ${result.failed} 件失敗。評価ケースのタブで内訳を確認してください。`;
                      })
                    }
                  >
                    {busy === 'check-draft' ? '採点中…' : '評価する'}
                  </button>
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={busy !== null}
                    onClick={() =>
                      void run('activate-draft', async () => {
                        await post(`/admin/rulebook/sets/${draft.id}/activate`, {});
                        return `v${draft.version_no} を有効化しました。次のチェックから適用されます。`;
                      })
                    }
                  >
                    この下書きを有効化
                  </button>
                </>
              )}
            </div>

            {/*
              New rules first, and separated.

              A draft is a copy of the live set plus whatever was approved into
              it, so a single list of thirteen rules hides the two that are
              actually being proposed. What an approver is deciding about is the
              difference, not the total.
            */}
            {/*
              The rules this draft would take away, first, because it is the one
              thing on the screen that happens by omission.
            */}
            {dropped.length > 0 && (
              <p className="alert alert--warn" role="status">
                有効化すると、本番にある
                <strong>{jaNumber(dropped.length)}件のルールが外れます</strong>：
                {dropped.map((rule) => rule.title).join('、')}。
                意図した削除ならこのままで構いません。
                意図していない場合は、この下書きが本番より古い可能性があります（作り直してください）。
              </p>
            )}

            {draft && (
              <>
                <p className="form__section">
                  下書き v{draft.version_no} で追加されたルール（{jaNumber(added.length)}）
                </p>
                {added.length === 0 ? (
                  <Empty>
                    まだ追加されていません。「候補」タブで承認するか、「ルールを追加」で手動で作成します。
                  </Empty>
                ) : (
                  <RuleTable rules={added} onOpen={setOpenRule} tone="new" />
                )}
              </>
            )}

            <p className="form__section">
              {draft
                ? `本番から引き継いだルール（${jaNumber(inherited.length)}）`
                : `本番セットのルール（${jaNumber(active.rules.length)}）`}
            </p>
            {draft ? (
              inherited.length === 0 ? (
                <Empty>引き継いだルールはありません。</Empty>
              ) : (
                <RuleTable rules={inherited} onOpen={setOpenRule} tone="kept" />
              )
            ) : active.rules.length === 0 ? (
              <Empty>ルールがありません。</Empty>
            ) : (
              <>
                <p className="muted small">
                  行を選ぶと下書きを作成（既にあれば再利用）して、そこで編集・削除できます。本番のルールは直接は変更できません。
                </p>
                <Table
                  head={
                    <>
                      <th scope="col">ルール</th>
                      <th scope="col">区分</th>
                      <th scope="col">根拠</th>
                    </>
                  }
                >
                  {active.rules.map((rule) => (
                    <tr key={rule.slug} {...rowOpen(() => void openActiveRule(rule))}>
                      <td>
                        {rule.title}
                        <span className="muted small" style={{ display: 'block' }}>
                          {rule.slug}
                        </span>
                      </td>
                      <td>
                        <Pill tone={rule.severity === 'REVIEW' ? 'warn' : 'muted'}>
                          {SEVERITY_LABEL[rule.severity] ?? rule.severity}
                        </Pill>
                      </td>
                      <td className="muted small">
                        {rule.law}
                        {rule.article && <span style={{ display: 'block' }}>{rule.article}</span>}
                      </td>
                    </tr>
                  ))}
                </Table>
              </>
            )}
          </>
        )}

        {/* ----------------------------------------------------------- cases */}
        {tab === 'cases' && (
          <>
            <div className="tenantbar">
              <button
                className="btn btn--sm"
                type="button"
                onClick={() => {
                  setNewCase({ text: '', expectSlug: '', shouldFlag: true });
                  setAddingCase(true);
                }}
              >
                ＋ 評価ケースを追加
              </button>
              <span className="muted small">
                下書きを有効化する前に、この文章で採点します。
                <strong>検出されてはいけない文</strong>も同じくらい重要です。
              </span>
            </div>

            {evalCases.items.length === 0 ? (
              <Empty>評価ケースがありません。合格の基準がないセットは有効化できません。</Empty>
            ) : (
              <Table
                head={
                  <>
                    <th scope="col">文章</th>
                    <th scope="col">期待</th>
                    <th scope="col">対象ルール</th>
                    <th scope="col" />
                  </>
                }
              >
                {evalCases.items.map((testCase) => (
                  <tr key={testCase.id}>
                    <td>{testCase.text}</td>
                    <td>
                      {testCase.should_flag ? (
                        <Pill tone="warn">指摘される</Pill>
                      ) : (
                        <Pill tone="ok">指摘されない</Pill>
                      )}
                    </td>
                    <td className="muted small">
                      {testCase.expect_slug ?? <span className="muted">—</span>}
                    </td>
                    <td>
                      <button
                        className="btn btn--ghost btn--sm"
                        type="button"
                        disabled={busy !== null}
                        onClick={() =>
                          void run(`case-${testCase.id}`, async () => {
                            await del(`/admin/rulebook/eval-cases/${testCase.id}`);
                            return '評価ケースを削除しました。';
                          })
                        }
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </Table>
            )}

            {/* The last scoring run, so a failure says which sentence and why. */}
            {check && (
              <>
                <p className="form__section">
                  直近の採点：{check.passed}/{check.total} 合格
                  {check.status === 'FAILED' && `（失敗 ${check.failed}）`}
                </p>
                <ul className="feed">
                  {check.results
                    .filter((result) => !result.passed)
                    .map((result) => (
                      <li key={result.id}>
                        <span className="feed__dot" style={{ background: 'var(--danger)' }} />
                        <span>
                          {result.text}
                          <span className="muted small" style={{ display: 'block' }}>
                            {result.shouldFlag
                              ? `指摘されるべきでしたが、一致したルールは ${
                                  result.firedSlugs.join('、') || 'ありません'
                                }`
                              : `指摘されるべきではないのに ${result.firedSlugs.join('、')} が一致しました`}
                          </span>
                        </span>
                      </li>
                    ))}
                </ul>
              </>
            )}
          </>
        )}

        {/* --------------------------------------------------------- history */}
        {tab === 'history' && (
          <Table
            head={
              <>
                <th scope="col">版</th>
                <th scope="col">状態</th>
                <th scope="col" className="num">
                  ルール
                </th>
                <th scope="col">作成・有効化</th>
                <th scope="col" />
              </>
            }
          >
            {sets.items.map((set) => (
              // The row opens the version; the buttons keep their own clicks.
              <tr
                key={set.id}
                {...rowOpen(() => {
                  setOpenSet(set);
                  setOpenSetRules(null);
                  void get<{ rules: Rule[] }>(`/admin/rulebook/sets/${set.id}`)
                    .then((body) => setOpenSetRules(body.rules))
                    .catch(() => setOpenSetRules([]));
                })}
              >
                <td>
                  v{set.version_no}
                  {set.note && (
                    <span className="muted small" style={{ display: 'block' }}>
                      {set.note}
                    </span>
                  )}
                </td>
                <td>
                  <Pill tone={STATUS_TONE[set.status] ?? 'muted'}>
                    {STATUS_LABEL[set.status] ?? set.status}
                  </Pill>
                </td>
                <td className="num">{jaNumber(set.rules)}</td>
                <td className="muted small">
                  {jaDate(set.created_at)}
                  {set.created_by_name ? `・${set.created_by_name}` : '・既定'}
                  {set.activated_at && (
                    <span style={{ display: 'block' }}>
                      有効化 {jaDate(set.activated_at)}
                      {set.activated_by_name ? `・${set.activated_by_name}` : ''}
                    </span>
                  )}
                </td>
                <td>
                  <div className="rowactions">
                    <button
                      className="btn btn--ghost btn--sm"
                      type="button"
                      disabled={busy !== null}
                      onClick={() =>
                        void run(`check-${set.id}`, async () => {
                          const result = await post<RuleCheckResult>(
                            `/admin/rulebook/sets/${set.id}/check`,
                            {},
                          );
                          setCheck(result);
                          setTab('cases');
                          return result.status === 'PASSED'
                            ? `v${set.version_no}: 評価ケース ${result.passed}/${result.total} 合格。有効化できます。`
                            : `v${set.version_no}: ${result.failed} 件失敗。内訳を確認してください。`;
                        })
                      }
                    >
                      {busy === `check-${set.id}` ? '採点中…' : '評価'}
                    </button>
                    {set.status !== 'ACTIVE' && (
                      <>
                        <button
                          className="btn btn--sm"
                          type="button"
                          disabled={busy !== null}
                          onClick={() =>
                            void run(`activate-${set.id}`, async () => {
                              await post(`/admin/rulebook/sets/${set.id}/activate`, {});
                              return `v${set.version_no} を有効化しました。次のチェックから適用されます。`;
                            })
                          }
                        >
                          有効化
                        </button>
                        <button
                          className="btn btn--ghost btn--sm"
                          type="button"
                          disabled={busy !== null}
                          onClick={() =>
                            void run(`delete-${set.id}`, async () => {
                              await del(`/admin/rulebook/sets/${set.id}`);
                              return `v${set.version_no} を削除しました。`;
                            })
                          }
                        >
                          削除
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      {openRule && (
        <RuleDetail
          rule={openRule}
          editable={draftRules.some((rule) => rule.id === openRule.id)}
          onClose={() => setOpenRule(null)}
          onChanged={() => void revalidator.revalidate()}
        />
      )}

      {openSet && (
        <Modal
          open
          onClose={() => setOpenSet(null)}
          title={`ルールセット v${openSet.version_no}`}
          caption={openSet.note || undefined}
          wide
        >
          <dl className="factgrid" style={{ borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
            <div className="factgrid__row">
              <dt>状態</dt>
              <dd>
                <Pill tone={STATUS_TONE[openSet.status] ?? 'muted'}>
                  {STATUS_LABEL[openSet.status] ?? openSet.status}
                </Pill>
              </dd>
            </div>
            <div className="factgrid__row">
              <dt>作成</dt>
              <dd>
                {jaDate(openSet.created_at)}
                {openSet.created_by_name && `・${openSet.created_by_name}`}
              </dd>
            </div>
            <div className="factgrid__row">
              <dt>有効化</dt>
              <dd>
                {openSet.activated_at ? (
                  <>
                    {jaDate(openSet.activated_at)}
                    {openSet.activated_by_name && `・${openSet.activated_by_name}`}
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
              </dd>
            </div>
            <div className="factgrid__row">
              <dt>ルール</dt>
              <dd>{jaNumber(openSet.rules)} 件</dd>
            </div>
          </dl>

          <p className="form__section">このセットのルール</p>
          {openSetRules === null ? (
            <p className="muted small">読み込み中…</p>
          ) : openSetRules.length === 0 ? (
            <Empty>ルールがありません。</Empty>
          ) : (
            <Table
              head={
                <>
                  <th scope="col">ルール</th>
                  <th scope="col">区分</th>
                  <th scope="col">根拠</th>
                </>
              }
            >
              {openSetRules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    {rule.title}
                    <span className="muted small" style={{ display: 'block' }}>
                      {rule.slug}
                    </span>
                  </td>
                  <td>
                    <Pill tone={rule.severity === 'REVIEW' ? 'warn' : 'muted'}>
                      {SEVERITY_LABEL[rule.severity] ?? rule.severity}
                    </Pill>
                  </td>
                  <td className="muted small">{rule.law}</td>
                </tr>
              ))}
            </Table>
          )}

          <div className="editpanel__actions">
            <button className="btn btn--ghost btn--sm" type="button" onClick={() => setOpenSet(null)}>
              閉じる
            </button>
          </div>
        </Modal>
      )}

      {/* A rule written by hand, for what the corpus has not said yet. */}
      <ModalForm
        open={addingRule}
        onClose={() => setAddingRule(false)}
        title="ルールを追加"
        caption="下書きに追加されます。有効化するまで記事チェックは変わりません。"
        submitLabel="追加する"
        wide
        onSubmit={async () => {
          const patterns = newRule.patterns
            .split('\n')
            .map((line) => line.trim())
            .filter(Boolean);
          if (patterns.length === 0) throw new Error('検出パターンを1つ以上入力してください');
          for (const pattern of patterns) {
            try {
              new RegExp(pattern);
            } catch {
              throw new Error(`正規表現が不正です: ${pattern}`);
            }
          }
          const setId = await workingDraft();
          await post(`/admin/rulebook/sets/${setId}/rules`, {
            slug: newRule.slug.trim(),
            title: newRule.title.trim(),
            guidance: newRule.guidance.trim(),
            fix: newRule.fix.trim(),
            severity: newRule.severity,
            patterns,
            law: newRule.law.trim(),
            article: newRule.article.trim(),
          });
        }}
      >
        <Field
          label="識別子"
          htmlFor="new-slug"
          required
          hint="英小文字とハイフン。指摘の同一性に使うため、あとから変更できません。"
        >
          <input
            id="new-slug"
            value={newRule.slug}
            maxLength={60}
            required
            placeholder="testimonial-as-evidence"
            onChange={(event) => setNewRule({ ...newRule, slug: event.target.value })}
          />
        </Field>
        <Field label="名称" htmlFor="new-title" required>
          <input
            id="new-title"
            value={newRule.title}
            maxLength={120}
            required
            placeholder="体験談を効果の根拠にしている"
            onChange={(event) => setNewRule({ ...newRule, title: event.target.value })}
          />
        </Field>
        <Field label="区分" htmlFor="new-severity">
          <Select
            id="new-severity"
            value={newRule.severity}
            onChange={(severity) => setNewRule({ ...newRule, severity })}
            options={[
              { value: 'REVIEW', label: '要確認（表現の是正が必要）' },
              { value: 'UNSUPPORTED', label: '根拠不足（裏づけの提示が必要）' },
            ]}
          />
        </Field>
        <Field label="指摘文" htmlFor="new-guidance" span="full" required>
          <Textarea
            id="new-guidance"
            minRows={2}
            maxLength={1000}
            value={newRule.guidance}
            onChange={(event) => setNewRule({ ...newRule, guidance: event.target.value })}
          />
        </Field>
        <Field
          label="検出パターン（1行に1つ）"
          htmlFor="new-patterns"
          span="full"
          required
          hint="日本語の正規表現。一般語だけのパターンは、正しい文まで指摘します。"
        >
          <Textarea
            id="new-patterns"
            minRows={3}
            maxLength={4000}
            value={newRule.patterns}
            onChange={(event) => setNewRule({ ...newRule, patterns: event.target.value })}
          />
        </Field>
        <Field label="根拠となる法令" htmlFor="new-law" required>
          <input
            id="new-law"
            value={newRule.law}
            maxLength={200}
            required
            onChange={(event) => setNewRule({ ...newRule, law: event.target.value })}
          />
        </Field>
        <Field label="条項" htmlFor="new-article">
          <input
            id="new-article"
            value={newRule.article}
            maxLength={300}
            placeholder="省令第1条の9第1号"
            onChange={(event) => setNewRule({ ...newRule, article: event.target.value })}
          />
        </Field>
      </ModalForm>

      <ModalForm
        open={addingCase}
        onClose={() => setAddingCase(false)}
        title="評価ケースを追加"
        caption="下書きを有効化する前に、この文章で採点します。"
        submitLabel="追加する"
        onSubmit={async () => {
          await post('/admin/rulebook/eval-cases', {
            text: newCase.text.trim(),
            shouldFlag: newCase.shouldFlag,
            ...(newCase.expectSlug.trim() ? { expectSlug: newCase.expectSlug.trim() } : {}),
          });
        }}
      >
        <Field label="文章" htmlFor="case-text" span="full" required>
          <Textarea
            id="case-text"
            minRows={2}
            maxLength={1000}
            value={newCase.text}
            onChange={(event) => setNewCase({ ...newCase, text: event.target.value })}
          />
        </Field>
        <Field
          label="期待する結果"
          htmlFor="case-expect"
          hint="「指摘されない」ケースが、広すぎるルールを止めます。"
        >
          <Select
            id="case-expect"
            value={newCase.shouldFlag ? 'flag' : 'clean'}
            onChange={(value) => setNewCase({ ...newCase, shouldFlag: value === 'flag' })}
            options={[
              { value: 'flag', label: '指摘される' },
              { value: 'clean', label: '指摘されない' },
            ]}
          />
        </Field>
        <Field label="対象ルール（任意）" htmlFor="case-slug" hint="どのルールで指摘されるべきか。">
          <input
            id="case-slug"
            value={newCase.expectSlug}
            maxLength={60}
            placeholder="superlative"
            onChange={(event) => setNewCase({ ...newCase, expectSlug: event.target.value })}
          />
        </Field>
      </ModalForm>
    </>
  );
}

/** The rule list, in the two groupings this screen cares about. */
function RuleTable({
  rules,
  onOpen,
  tone,
}: {
  rules: Rule[];
  onOpen: (rule: Rule) => void;
  /** 'new' marks what this draft proposes; 'kept' what it inherited. */
  tone: 'new' | 'kept';
}) {
  return (
    <Table
      head={
        <>
          <th scope="col">ルール</th>
          <th scope="col">区分</th>
          <th scope="col">検出パターン</th>
          <th scope="col">根拠</th>
        </>
      }
    >
      {rules.map((rule) => (
        <tr key={rule.id} {...rowOpen(() => onOpen(rule))}>
          <td>
            {rule.title}
            <span className="muted small" style={{ display: 'block' }}>
              {rule.slug}
              {tone === 'new' && (
                <>
                  {' '}
                  <Pill tone="ok">新規</Pill>
                </>
              )}
            </span>
          </td>
          <td>
            <Pill tone={rule.severity === 'REVIEW' ? 'warn' : 'muted'}>
              {SEVERITY_LABEL[rule.severity] ?? rule.severity}
            </Pill>
          </td>
          <td className="muted small">
            {rule.patterns.slice(0, 2).map((pattern) => (
              <code key={pattern} className="small" style={{ display: 'block' }}>
                {pattern.length > 40 ? `${pattern.slice(0, 40)}…` : pattern}
              </code>
            ))}
            {rule.patterns.length > 2 && `ほか ${rule.patterns.length - 2} 件`}
          </td>
          <td className="muted small">
            {rule.law}
            {rule.article && <span style={{ display: 'block' }}>{rule.article}</span>}
          </td>
        </tr>
      ))}
    </Table>
  );
}
